import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { infoCircleIcon, moreVerticalIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import DataTable from 'datatables.net-dt';
import { AnalyticsService, ChartData } from '../../../../../../../core/services/analytics.service';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { DonutChartConfig } from '../../../../../../../core/charts/configs';
import { getAnalyticsColor } from '../../../../../../../core/charts/utils/chart-colors';
import { environment } from '../../../../../../../../environments/environment';
import { EdrCsvExportService } from '../../services/edr-csv-export.service';
import { formatDateRangeLabel } from '../../../../../../../core/shared/common/date-range.util';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-agent-status',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgxEchartsModule,
    SkeletonModule,
    DialogsModule,
    ButtonsModule,
    NotificationModule,
    SVGIconModule,
    TooltipModule,
    DatatableComponent,
  ],
  templateUrl: './agent-status.component.html',
  styleUrl: './agent-status.component.scss',
  providers: [AnalyticsService],
})
export class AgentStatusComponent {
  private csvExport = inject(EdrCsvExportService);

  private locale = inject(LOCALE_ID);

  /** Report type key for this view's CSV export. */
  private readonly reportKey = 'edr-agent-status-report';

  /** True while a CSV export is being queued. */
  public isExporting = signal<boolean>(false);

  public status = input<ChartData[]>([]);
  public organizationId = input<number | null>(null);
  public dateRange = input<string[] | null>(null);
  public isLoading = input<boolean>(false);

  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const label = formatDateRangeLabel(this.dateRange());
    return label ? `Agent Connection Status (${label})` : 'Agent Connection Status';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/analytics/administrative/edragentstatusdetails(${id})` : '';
  });

  public datatableColumns = [
    {
      data: 'timestamp',
      name: 'timestamp',
      title: 'Last Observed At',
      type: 'string',
      render: (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : ''),
    },
    { data: 'agent_id', name: 'agent_id', title: 'Agent ID', type: 'string', render: text },
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_version', name: 'agent_version', title: 'Agent Version', type: 'string', render: text },
    { data: 'device_type', name: 'device_type', title: 'Endpoint | Device Type', type: 'string', render: text },
    { data: 'network_status', name: 'network_status', title: 'Network Status', type: 'string', render: text },
    { data: 'group_name', name: 'group_name', title: 'Group Name', type: 'string', render: text },
    { data: 'vendor', name: 'vendor', title: 'Vendor', type: 'string', render: text },
  ];

  public chartOptions = computed((): EChartsOption => {
    const data = this.status().map((item, index) => ({
      name: item.description || 'Unknown',
      value: item.count,
      color: item.color || getAnalyticsColor(index),
    }));
    return DonutChartConfig.create({ data, innerRadius: '50%', outerRadius: '75%' });
  });


  private filterDescriptor = computed((): CompositeFilterDescriptor => {
    const filters: FilterDescriptor[] = [];
    const range = this.dateRange();
    if (range) {
      filters.push({ field: 'timestamp', operator: 'gt', value: range[0] });
      filters.push({ field: 'timestamp', operator: 'lt', value: range[1] });
    }
    return { logic: 'and', filters };
  });


  public odataFilters = computed((): Record<string, string> => {
    const expression = this.csvExport.buildFilterExpression(this.filterDescriptor());
    return expression ? { $filter: encodeURIComponent(expression) } : {};
  });

  onViewData(): void {
    this.showPopup.set(true);
  }

  exportCsv(): void {
    this.isExporting.set(true);
    this.csvExport
      .export({
        reportKey: this.reportKey,
        organizationId: this.organizationId(),
        dateRange: this.dateRange(),
        filter: this.filterDescriptor(),
      })
      .subscribe({
        next: () => this.isExporting.set(false),
        error: () => this.isExporting.set(false),
      });
  }

  close(): void {
    this.showPopup.set(false);
  }
}
